import { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../../config/firebase';
import { Spinner } from '../../components/ui/Spinner';
import { useShoppingCart } from '../../contexts/ShoppingContext';
import ServiceCard from '../../components/services/ServiceCard';
import OrderConfirmationDialog from '../../components/store/OrderConfirmationDialog';
import { fetchGoogleReviews } from '../../services/googlePlaces';

const ServicesPage = () => {
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { cart, clearCart } = useShoppingCart();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [googleRating, setGoogleRating] = useState(4.8);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesRef = ref(db, 'services');
        const snapshot = await get(servicesRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          setServices(data);
          console.log('Fetched services:', data);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching services:', err);
        setError('Failed to load services');
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const loadGoogleReviews = async () => {
      const data = await fetchGoogleReviews();
      setGoogleReviews(data.reviews);
      setGoogleRating(data.rating);
    };

    loadGoogleReviews();
  }, []);

  const handleOrder = (formData) => {
    const cartItems = Object.entries(cart).map(([serviceId, quantity]) => {
      const category = Object.values(services).find((cat) =>
        Object.keys(cat.items).includes(serviceId),
      );
      const service = category?.items[serviceId];
      return `${service.name} x${quantity} = Rs. ${(
        service.price * quantity
      ).toFixed(2)} (${service.duration})`;
    });

    const total = Object.entries(cart).reduce((sum, [serviceId, quantity]) => {
      const category = Object.values(services).find((cat) =>
        Object.keys(cat.items).includes(serviceId),
      );
      const service = category?.items[serviceId];
      return sum + (service?.price || 0) * quantity;
    }, 0);

    const message = `Hi, I would like to book these services:\n\n${cartItems.join(
      '\n',
    )}\n\nTotal: Rs. ${total.toFixed(2)}\n\nAppointment Details:\nDate: ${
      formData.formattedDate
    }\nTime: ${formData.time}\n\nCustomer Details:\nName: ${
      formData.name
    }\nPhone: ${formData.phone}`;

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/94743633914?text=${encodedMessage}`, '_blank');
    clearCart();
    setIsDialogOpen(false);
  };

  const filteredReviews = googleReviews.filter((review) => review.rating >= 4);

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  if (error) return <div className="text-center text-red-600 p-4">{error}</div>;

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-pink-50/90 via-white to-rose-50/50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-800">
              Brissbella VIP Unisex Salon
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Experience luxury beauty treatments and professional care at
              Kandy's premier salon. Our expert team delivers exceptional
              service for both men and women.
            </p>
          </div>
        </div>
      </section>

      {/* Stats/Features Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            <div className="p-4 sm:p-6 bg-primary-50 rounded-lg">
              <div className="text-2xl sm:text-4xl font-bold text-primary-600 mb-2">
                10+
              </div>
              <div className="text-sm sm:text-base text-gray-600">
                Years Experience
              </div>
            </div>
            <div className="p-4 sm:p-6 bg-primary-50 rounded-lg">
              <div className="text-2xl sm:text-4xl font-bold text-primary-600 mb-2">
                1000+
              </div>
              <div className="text-sm sm:text-base text-gray-600">
                Happy Clients
              </div>
            </div>
            <div className="p-4 sm:p-6 bg-primary-50 rounded-lg">
              <div className="text-2xl sm:text-4xl font-bold text-primary-600 mb-2">
                {googleRating}★
              </div>
              <div className="text-sm sm:text-base text-gray-600">
                75+ Google Reviews
              </div>
            </div>
            <div className="p-4 sm:p-6 bg-primary-50 rounded-lg">
              <div className="text-2xl sm:text-4xl font-bold text-primary-600 mb-2">
                100%
              </div>
              <div className="text-sm sm:text-base text-gray-600">
                Satisfaction Rate
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Experience the perfect blend of traditional and modern beauty
              treatments, delivered by our expert team of professionals.
            </p>
          </div>

          {/* Services Categories */}
          <div className="space-y-16">
            {Object.entries(services).map(([categoryId, category]) => (
              <div key={categoryId}>
                <h3 className="text-2xl font-bold mb-8 text-center">
                  {category.name}
                </h3>

                {/* Category Services Slider */}
                <div className="relative">
                  <div className="overflow-x-auto pb-4">
                    <div className="flex gap-6">
                      {category.items &&
                        Object.entries(category.items).map(
                          ([serviceId, service]) => (
                            <div
                              key={serviceId}
                              className="w-[350px] flex-shrink-0"
                            >
                              <ServiceCard
                                service={{
                                  id: serviceId,
                                  ...service,
                                  categoryName: category.name,
                                }}
                              />
                            </div>
                          ),
                        )}
                    </div>
                  </div>

                  {/* Optional: Add navigation arrows if needed */}
                  {Object.keys(category.items || {}).length > 3 && (
                    <>
                      <button
                        className="absolute top-1/2 -left-4 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-lg text-gray-600 hover:text-primary-600"
                        onClick={() => {
                          // Add scroll logic if needed
                        }}
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <button
                        className="absolute top-1/2 -right-4 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-lg text-gray-600 hover:text-primary-600"
                        onClick={() => {
                          // Add scroll logic if needed
                        }}
                      >
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Cart Button */}
          {Object.keys(cart).length > 0 && (
            <div className="fixed bottom-4 right-4 z-50">
              <button
                onClick={() => setIsDialogOpen(true)}
                className="bg-green-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <span className="font-medium">
                  Book Now ({Object.values(cart).reduce((a, b) => a + b, 0)})
                </span>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-gray-600">
              Real reviews from our valued customers
            </p>
          </div>
          <div className="relative overflow-hidden">
            <div className="review-slider flex">
              {filteredReviews.map((review, index) => (
                <div
                  key={`${review.id}-${index}`}
                  className="min-w-[280px] xs:min-w-[320px] sm:min-w-[380px] md:min-w-[400px] p-3 sm:p-4"
                >
                  <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg h-full">
                    <div className="flex items-center mb-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 sm:w-12 h-10 sm:h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-lg font-semibold">
                          {review.author_name?.charAt(0) || 'A'}
                        </div>
                      </div>
                      <div className="ml-3 sm:ml-4">
                        <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                          {review.author_name}
                        </h4>
                        <div className="flex text-yellow-400 mt-1">
                          {[...Array(review.rating)].map((_, i) => (
                            <svg
                              key={i}
                              className="w-3 h-3 sm:w-4 sm:h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-gray-600 line-clamp-4">
                      {review.text}
                    </p>
                    <p className="mt-2 text-xs sm:text-sm text-gray-400">
                      {new Date(review.time * 1000).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Working Hours Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6">Working Hours</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Monday</span>
                  <span className="font-medium text-red-500">Closed</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Tuesday - Saturday</span>
                  <span className="font-medium">8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Sunday</span>
                  <span className="font-medium">8:00 AM - 6:00 PM</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6">Book an Appointment</h3>
              <p className="text-gray-600 mb-6">
                Reserve your slot now and experience our premium services.
              </p>
              <div className="space-y-4">
                <a
                  href="tel:+94743633914"
                  className="flex items-center text-gray-700 hover:text-primary-600"
                >
                  <span className="mr-2">📞</span>
                  074 363 3914
                </a>
                <a
                  href="https://wa.me/94743633914"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-gray-700 hover:text-green-600"
                >
                  <span className="mr-2">💬</span>
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Visit Us</h2>
            <p className="text-gray-600">
              Located in the heart of Kandy, we're easily accessible and ready
              to serve you
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Map */}
            <div className="lg:col-span-2">
              <div className="rounded-lg overflow-hidden shadow-lg">
                <iframe
                  title="Brissbella VIP Unisex Salon map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d988.4023055766892!2d80.62357729999999!3d7.2828039!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae3694ae39c9163%3A0xc8b98714351b72a!2sBrissbella%20VIP%20Unisex%20Salon!5e0!3m2!1sen!2slk!4v1710338000000!5m2!1sen!2slk"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold mb-6">Location & Contact</h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Address</h4>
                  <p className="text-gray-600">
                    No 303/1 William Gopallawa Mawatha,
                    <br />
                    Kandy 20000
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Contact</h4>
                  <div className="space-y-3">
                    <a
                      href="tel:+94743633914"
                      className="flex items-center text-gray-600 hover:text-primary-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      074 363 3914
                    </a>
                    <a
                      href="https://wa.me/94743633914"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-600 hover:text-green-600 transition-colors"
                    >
                      <svg
                        className="w-5 h-5 mr-2"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      WhatsApp Us
                    </a>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">
                    Get Directions
                  </h4>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=7.2826594,80.6244567&destination_place_id=ChIJY5Gc4KQ5zR4RoHIbNRGHuMg"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                      />
                    </svg>
                    Get Directions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <OrderConfirmationDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        cart={cart}
        products={services}
        onConfirm={handleOrder}
        type="service"
      />
    </div>
  );
};

export default ServicesPage;
